"use client";
import { lazy, useCallback, useEffect, useMemo, useState } from "react";
import { NavbarProps } from "./Navbar";

import { useAtomValue } from "jotai";
import styled, { keyframes } from "styled-components";
import useOnLogout from "../../hooks/useOnLogout";
import { MenuIcon } from "../../icons/MenuIcon";
import { userAtom } from "../../store/userAtom";
import { defaultTheme } from "../../styles/defaultTheme";
import { FlexRow, shadow, sizes } from "../../styles/globalsStyles";
import { RouteInfo } from "../../utils/clientRoutes";
import { StyledLink } from "../StyledLink";
import { Button } from "../core/Button";
import { StyledNavbar } from "./navbar.styles";
const UserNameToolbar = lazy(() => import("./UserNameToolbar"));
const slideInFromRight = keyframes`
  from {
    top: -300px;
  }
  to {
    top: ${sizes.navbarHeight}px;
  }
`;

const Menu = styled.div`
  width: 100%;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  left: 50%;
  transform: translateX(-50%);
  border: 1px solid ${defaultTheme.tertiaryColor};
  background-color: #fff;
  position: absolute;
  animation: ${slideInFromRight} 0.4s ease-in-out;
  ${shadow}
  z-index: 9;
`;
const LinksWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
`;
const LinksAndImageWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: auto;
`;

const NavbarMobile = ({ routes, image }: NavbarProps) => {
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const [navbarRoutes, setNavbarRoutes] =
    useState<(RouteInfo & { isSubMenu?: boolean })[]>(routes);
  const { logout } = useOnLogout();
  useEffect(() => {
    setNavbarRoutes(routes);
  }, [routes]);
  const user = useAtomValue(userAtom);
  const isSubMenuOpened = useMemo(
    () => navbarRoutes.find((r) => r.isSubMenu),
    [navbarRoutes]
  );

  const removeSubMenu = useCallback(() => {
    if (isSubMenuOpened) {
      setNavbarRoutes((state) => {
        const updatedRoutes = [...state];

        return updatedRoutes.filter((r) => !r.isSubMenu);
      });
    }
  }, [isSubMenuOpened]);

  const handleMenuClick = useCallback(() => {
    setIsMenuOpened((state) => {
      if (state) {
        removeSubMenu();
      }
      return !state;
    });
  }, [removeSubMenu]);

  const handleOpenSubMenu = useCallback(
    (
      routeInfo: RouteInfo,
      e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
      index: number
    ) => {
      e.preventDefault();
      if (!routeInfo.subRoutes) return;
      if (isSubMenuOpened) {
        removeSubMenu();
        return;
      }
      const subRoutes = routeInfo.subRoutes.map((ri) => ({
        ...ri,
        isSubMenu: true
      }));

      setNavbarRoutes((state) => {
        const updatedRoutes = [...state];

        if (subRoutes) {
          updatedRoutes.splice(index + 1, 0, ...subRoutes);
        }

        return updatedRoutes;
      });
    },
    [isSubMenuOpened, removeSubMenu]
  );

  const handleRouteClick = useCallback(
    (
      routeInfo: RouteInfo,
      e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
      index: number
    ) => {
      if (routeInfo.subRoutes) {
        handleOpenSubMenu(routeInfo, e, index);
      } else {
        handleMenuClick();
      }
    },
    [handleMenuClick, handleOpenSubMenu]
  );

  return (
    <>
      <StyledNavbar $isMobile>
        <MenuIcon onClick={handleMenuClick} color={defaultTheme.primaryColor} />
        {image && (
          <FlexRow $alignItems="center" $gap={"1rem"}>
            {user && <UserNameToolbar />}
            {image}
          </FlexRow>
        )}
      </StyledNavbar>
      {isMenuOpened && (
        <Menu>
          <LinksAndImageWrapper>
            <LinksWrapper>
              {navbarRoutes.map((route, index) => (
                <StyledLink
                  onClick={(e) => handleRouteClick(route, e, index)}
                  route={route}
                  key={`${index}--allRoutes-navbar-mobile`}
                />
              ))}
              {user && (
                <Button
                  onClick={logout}
                  // route={route}
                  // route={""}
                  // key={`${index}--allRoutes-navbar-mobile`}
                >
                  התנתקות
                </Button>
              )}
            </LinksWrapper>
          </LinksAndImageWrapper>
        </Menu>
      )}
    </>
  );
};

export default NavbarMobile;
